import { useState } from "react";
import { RotateCcw, Trash2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { useTranslator } from "@/hooks/useTranslator";
import { TranscriptPanel } from "@/components/TranscriptPanel";
import { QuickPhrasesSheet } from "@/components/QuickPhrasesSheet";
import { TopBar } from "@/components/TopBar";
import { LANGS } from "@/lib/api";

const IconBtn = ({ onClick, testId, active, children, title }) => (
  <button
    type="button"
    title={title}
    data-testid={testId}
    onClick={onClick}
    className={`flex h-11 w-11 items-center justify-center rounded-full border transition-colors ${
      active ? "border-amber-400 bg-amber-500/20 text-amber-300" : "border-slate-600 bg-slate-800/80 text-white hover:bg-slate-700"
    }`}
  >
    {children}
  </button>
);

export default function SingleDevice() {
  const [rotated, setRotated] = useState(true);
  const [fontScale, setFontScale] = useState(1);
  const { segments, addUtterance, addPhrase, setPending, clear } = useTranslator();

  const es = useSpeechRecognition({ lang: LANGS.es.speech, onFinal: (t, r) => addUtterance("es", t, {}, r), onInterim: (t) => setPending("es", t) });
  const ar = useSpeechRecognition({ lang: LANGS.ar.speech, onFinal: (t, r) => addUtterance("ar", t, {}, r), onInterim: (t) => setPending("ar", t) });

  const micEs = () => {
    if (!es.supported) return toast.error("Este navegador no soporta reconocimiento de voz. Usa Chrome o Edge.");
    ar.stop();
    es.toggle();
  };
  const micAr = () => {
    if (!ar.supported) return toast.error("Este navegador no soporta reconocimiento de voz. Usa Chrome o Edge.");
    es.stop();
    ar.toggle();
  };

  if (es.error === "not-allowed" || ar.error === "not-allowed") toast.error("Permiso de micrófono denegado.", { id: "mic-denied" });

  return (
    <div className="flex h-full flex-col bg-[#0b132b] text-white" data-testid="single-device-page">
      <TopBar title="Un dispositivo · cara a cara" />
      <div className="grid min-h-0 flex-1 grid-rows-[1fr_auto_1fr]">
        <TranscriptPanel
          lang="es"
          segments={segments}
          listening={es.listening}
          onMic={micEs}
          fontScale={fontScale}
          testId="transcript-spanish-container"
          micTestId="mic-button-spanish"
          accent="text-blue-300"
        />
        <div className="flex items-center justify-center gap-2 border-y border-slate-700/70 bg-[#1c2541] px-3 py-2" data-testid="center-toolbar">
          <IconBtn onClick={() => setRotated((r) => !r)} testId="rotate-darija-panel-toggle" active={rotated} title="Girar 180°">
            <RotateCcw className="h-5 w-5" />
          </IconBtn>
          <QuickPhrasesSheet onPick={(p) => addPhrase("es", p.es, p.ar)} />
          <IconBtn onClick={() => setFontScale((f) => Math.max(0.8, f - 0.15))} testId="font-smaller-button" title="Texto más pequeño">
            <Minus className="h-5 w-5" />
          </IconBtn>
          <IconBtn onClick={() => setFontScale((f) => Math.min(1.8, f + 0.15))} testId="font-bigger-button" title="Texto más grande">
            <Plus className="h-5 w-5" />
          </IconBtn>
          <IconBtn onClick={clear} testId="clear-transcript-button" title="Borrar conversación">
            <Trash2 className="h-5 w-5" />
          </IconBtn>
        </div>
        <TranscriptPanel
          lang="ar"
          segments={segments}
          listening={ar.listening}
          onMic={micAr}
          rotated={rotated}
          fontScale={fontScale}
          testId="transcript-darija-container"
          micTestId="mic-button-darija"
          accent="text-amber-300"
        />
      </div>
    </div>
  );
}
